import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, RefreshCw, Upload, Download, FileText, X, AlertCircle } from 'lucide-react';
import { sickLeaveRequestApi } from '../services/api';
import { SickLeaveRequest } from '../types';
import { useAuth } from '../context/AuthContext';
import { formatCompanyDate } from '../utils/companyDateFormat';
import { triggerBlobDownload } from '../utils/certificateFiles';

const MySickLeave: React.FC = () => {
  const { employee } = useAuth();
  const [requests, setRequests] = useState<SickLeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState('');
  const [pendingUploadId, setPendingUploadId] = useState<number | null>(null);
  const pendingUploadIdRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!employee?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await sickLeaveRequestApi.getAll(employee.id);
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [employee?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee?.id) return;
    setFormError('');
    if (!startDate || !endDate) {
      setFormError('Start and end dates are required.');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setFormError('End date must be on or after start date.');
      return;
    }
    try {
      setSubmitting(true);
      await sickLeaveRequestApi.create({
        employeeId: employee.id,
        startDate,
        endDate,
        reason: reason.trim() || undefined,
        status: 'Submitted',
      });
      setShowForm(false);
      setStartDate('');
      setEndDate('');
      setReason('');
      await load();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Failed to submit sick leave.');
    } finally {
      setSubmitting(false);
    }
  };

  const triggerUpload = (id: number) => {
    pendingUploadIdRef.current = id;
    setPendingUploadId(id);
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('data-sick-request-id', String(id));
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const fromInput = Number(e.currentTarget.getAttribute('data-sick-request-id') || '');
    const requestId = fromInput || pendingUploadIdRef.current || pendingUploadId;
    e.target.value = '';
    if (!file || !requestId) return;
    try {
      setUploadingId(requestId);
      await sickLeaveRequestApi.uploadCertificate(requestId, file);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to upload certificate.');
    } finally {
      setUploadingId(null);
      pendingUploadIdRef.current = null;
      setPendingUploadId(null);
    }
  };

  const downloadCertificate = async (req: SickLeaveRequest) => {
    try {
      const blob = await sickLeaveRequestApi.downloadCertificate(req.sickLeaveRequestId, 'attachment');
      triggerBlobDownload(blob, req.certificateFileName || 'sick-certificate.pdf');
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Could not download certificate.');
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <input
        ref={fileInputRef}
        type="file"
        data-testid="sick-certificate-file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
        className="hidden"
        onChange={handleFileSelected}
      />

      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">My Sick Leave</h1>
          <p className="text-sm text-neutral-500 mt-1">Report absences and upload medical certificates when required.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Report sick leave
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-12 text-center">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center text-neutral-500">
          No sick leave requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div
              key={req.sickLeaveRequestId}
              data-sick-request-card
              data-sick-request-id={req.sickLeaveRequestId}
              className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-semibold text-neutral-900">
                    {formatCompanyDate(req.startDate)}
                    {req.startDate !== req.endDate && <> – {formatCompanyDate(req.endDate)}</>}
                  </div>
                  <div className="text-sm text-neutral-600 mt-1">{req.totalDays} day{req.totalDays === 1 ? '' : 's'}</div>
                  {req.reason && <p className="text-sm text-neutral-700 mt-2">{req.reason}</p>}
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                  req.status === 'Approved' ? 'bg-green-50 text-green-700 border-green-200' :
                  req.status === 'Rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                  req.status === 'Submitted' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                  'bg-neutral-50 text-neutral-700 border-neutral-200'
                }`}>
                  {req.status}
                </span>
              </div>

              <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap items-center gap-3 text-sm">
                {req.certificateRequired ? (
                  <span className={`inline-flex items-center gap-1 ${req.certificateUploaded ? 'text-green-700' : 'text-amber-700'}`}>
                    {req.certificateUploaded ? <FileText className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    {req.certificateUploaded ? 'Certificate uploaded' : 'Certificate required'}
                  </span>
                ) : (
                  <span className="text-neutral-500">Certificate not required</span>
                )}

                {req.certificateRequired && !req.certificateUploaded && req.status !== 'Rejected' && req.status !== 'Approved' && (
                  <button
                    type="button"
                    data-sick-request-id={req.sickLeaveRequestId}
                    onClick={() => triggerUpload(req.sickLeaveRequestId)}
                    disabled={uploadingId === req.sickLeaveRequestId}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {uploadingId === req.sickLeaveRequestId ? 'Uploading…' : 'Upload certificate'}
                  </button>
                )}

                {req.certificateUploaded && (
                  <>
                    {req.status !== 'Rejected' && req.status !== 'Approved' && (
                      <button
                        type="button"
                        data-sick-request-id={req.sickLeaveRequestId}
                        onClick={() => triggerUpload(req.sickLeaveRequestId)}
                        disabled={uploadingId === req.sickLeaveRequestId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Replace
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => downloadCertificate(req)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </button>
                    {req.certificateFileName && (
                      <span className="text-xs text-neutral-500 truncate max-w-[200px]">{req.certificateFileName}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center px-5 py-4 border-b border-neutral-200">
              <h2 className="text-lg font-semibold">Report sick leave</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {formError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{formError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start date *</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End date *</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Reason</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="w-full px-3 py-2 border border-neutral-300 rounded-md" placeholder="Optional" />
              </div>
              <p className="text-xs text-neutral-500">
                If your absence is longer than your company&apos;s certificate threshold, you will be asked to upload a medical certificate after submitting.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-neutral-300 rounded-md">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySickLeave;
